import { motion } from "framer-motion";
import PipelineVisual from "@/components/dendros/PipelineVisual";
import WaitlistForm from "@/components/dendros/WaitlistForm";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden"
        >
            {/* Background grid + volt glow */}
            <div className="absolute inset-0 bg-grid pointer-events-none opacity-70" />
            <div
                className="absolute top-20 -left-40 h-[520px] w-[520px] rounded-full blur-[120px] pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle, rgba(16,185,129,0.20), transparent 65%)",
                }}
            />
            <div
                className="absolute top-40 right-0 h-[420px] w-[420px] rounded-full blur-[100px] pointer-events-none opacity-70"
                style={{
                    background:
                        "radial-gradient(circle, rgba(16,185,129,0.09), transparent 70%)",
                }}
            />

            <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
                    {/* Left: copy */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7"
                    >
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] uppercase tracking-[0.2em] font-mono text-neutral-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" />
                            Dendros
                        </div>

                        <h1 className="mt-6 font-display font-semibold text-[44px] sm:text-[56px] lg:text-[72px] leading-[0.98] tracking-[-0.03em]">
                            Stop your reps from{" "}
                            <span className="relative inline-block">
                                <span className="text-[#10B981]">chasing</span>
                                <svg
                                    className="absolute -bottom-2 left-0 w-full"
                                    viewBox="0 0 200 8"
                                    preserveAspectRatio="none"
                                >
                                    <path
                                        d="M2 5 Q 50 1, 100 4 T 198 3"
                                        stroke="#10B981"
                                        strokeWidth="1.5"
                                        fill="none"
                                        strokeLinecap="round"
                                        opacity="0.5"
                                    />
                                </svg>
                            </span>{" "}
                            <span className="text-neutral-400 italic font-light">
                                ghosts.
                            </span>
                        </h1>

                        <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-neutral-400">
                            30% of your CRM data is already dead. Dendros
                            automatically purges inactive decision-makers from
                            your Salesforce — so your team only spends cycles on{" "}
                            <span className="text-white">real revenue.</span>
                        </p>

                        <div className="mt-8 max-w-lg">
                            <WaitlistForm
                                source="hero"
                                testIdPrefix="hero"
                                buttonLabel="Schedule a Demo"
                            />
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-[12px] text-neutral-500">
                            <span className="flex items-center gap-2">
                                <Dot /> Salesforce-native
                            </span>
                            <span className="flex items-center gap-2">
                                <Dot /> Real-time identity signals
                            </span>
                            <span className="flex items-center gap-2">
                                <Dot /> BYOK · zero-burn
                            </span>
                            <span className="flex items-center gap-2">
                                <Dot /> SOC-2 roadmap
                            </span>
                        </div>
                    </motion.div>

                    {/* Right: animated pipeline visual */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="lg:col-span-5"
                    >
                        <PipelineVisual />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function Dot() {
    return <span className="h-1 w-1 rounded-full bg-[#10B981]" />;
}
